import {
  getQuantityDiscountTriggerQuantity,
  normalizeQuantityDiscount,
} from 'src/shared/discountShop'
import type { QuantityDiscount } from 'src/shared/discountShop'

export type QuantityDiscountText = {
  title: string
  progress?: string
  active?: boolean
  actionLabel?: string
}

const formatValue = (value: number) =>
  Number.isInteger(value)
    ? String(value)
    : String(value).replace('.', ',')

const formatPercentValue = (value: number) =>
  `${formatValue(value)}%`

const getPaidQuantity = (quantityDiscount: QuantityDiscount) =>
  Math.max(
    0,
    (quantityDiscount.requiredQuantity || 0) - (quantityDiscount.rewardQuantity || 0),
  )

const getApplications = (qty: number, quantityDiscount: QuantityDiscount) => {
  const triggerQuantity = getQuantityDiscountTriggerQuantity(quantityDiscount)
  if (triggerQuantity <= 0 || qty < triggerQuantity) return 0
  if (quantityDiscount.applyTo === 'allItems') return 1

  return quantityDiscount.repeating === false
    ? 1
    : Math.floor(qty / triggerQuantity)
}

const getDiscountedRewardQuantity = (qty: number, quantityDiscount: QuantityDiscount) => {
  if (quantityDiscount.applyTo === 'allItems') return qty

  const applications = getApplications(qty, quantityDiscount)
  return applications * (quantityDiscount.rewardQuantity || 0)
}

const buildTitle = (quantityDiscount: QuantityDiscount) => {
  const requiredQuantity = quantityDiscount.requiredQuantity || 0
  const rewardQuantity = quantityDiscount.rewardQuantity || 0

  if (quantityDiscount.rewardType === 'freeItems') {
    return `${requiredQuantity} kaufen, nur ${getPaidQuantity(quantityDiscount)} zahlen`
  }

  const percent = formatPercentValue(quantityDiscount.rewardPercent || 0)

  if (quantityDiscount.applyTo === 'allItems') {
    return `Ab ${requiredQuantity} Stück ${percent} Rabatt`
  }

  return `${requiredQuantity} kaufen + ${rewardQuantity} Stück mit ${percent} Rabatt`
}

export const getQuantityDiscountBadgeLabel = (
  quantityDiscount?: QuantityDiscount,
) => {
  const normalizedQuantityDiscount = normalizeQuantityDiscount(quantityDiscount)
  if (!normalizedQuantityDiscount) return undefined

  const requiredQuantity = normalizedQuantityDiscount.requiredQuantity || 0
  const rewardQuantity = normalizedQuantityDiscount.rewardQuantity || 0

  if (normalizedQuantityDiscount.rewardType === 'freeItems') {
    return `Aktion ${requiredQuantity} für ${getPaidQuantity(normalizedQuantityDiscount)}`
  }

  const percent = formatPercentValue(normalizedQuantityDiscount.rewardPercent || 0)

  if (normalizedQuantityDiscount.applyTo === 'allItems') {
    return `Aktion ab ${requiredQuantity} -${percent}`
  }

  return `Aktion ${requiredQuantity} + ${rewardQuantity} -${percent}`
}

const buildRequirementText = (quantityDiscount: QuantityDiscount) => {
  const triggerQuantity = getQuantityDiscountTriggerQuantity(quantityDiscount)

  if (quantityDiscount.rewardType === 'freeItems') {
    return `Ab ${triggerQuantity} Stück im Warenkorb`
  }

  if (quantityDiscount.applyTo === 'allItems') {
    return `Gilt ab ${triggerQuantity} Stück`
  }

  return `Ab ${triggerQuantity} Stück im Warenkorb`
}

const buildActiveText = (
  qty: number,
  quantityDiscount: QuantityDiscount,
) => {
  const discountedQty = getDiscountedRewardQuantity(qty, quantityDiscount)

  if (quantityDiscount.rewardType === 'freeItems') {
    return `${discountedQty} Stück gratis in dieser Position`
  }

  const percent = formatPercentValue(quantityDiscount.rewardPercent || 0)

  if (quantityDiscount.applyTo === 'allItems') {
    return `${percent} Rabatt auf ${qty} Stück aktiv`
  }

  return `${percent} Rabatt auf ${discountedQty} Stück aktiv`
}

export const getQuantityDiscountText = (
  quantityDiscount?: QuantityDiscount,
  qty?: number,
): QuantityDiscountText | undefined => {
  const normalizedQuantityDiscount = normalizeQuantityDiscount(quantityDiscount)
  if (!normalizedQuantityDiscount) return undefined

  const title = buildTitle(normalizedQuantityDiscount)
  if (typeof qty !== 'number') {
    return {
      title,
      progress: buildRequirementText(normalizedQuantityDiscount),
      active: false,
    }
  }

  const safeQty = Math.max(0, Math.floor(qty))
  const triggerQuantity = getQuantityDiscountTriggerQuantity(normalizedQuantityDiscount)
  const applications = getApplications(safeQty, normalizedQuantityDiscount)

  if (applications <= 0) {
    const missingQty = Math.max(0, triggerQuantity - safeQty)

    return {
      title,
      progress: normalizedQuantityDiscount.rewardType === 'freeItems'
        ? `Noch ${missingQty} Stück bis zur Gratis-Aktion`
        : `Noch ${missingQty} Stück bis zum Aktionsrabatt`,
      active: false,
    }
  }

  return {
    title,
    progress: buildActiveText(safeQty, normalizedQuantityDiscount),
    active: true,
  }
}

const formatPieceCount = (qty: number) =>
  qty === 1
    ? '1 Stück'
    : `${qty} Stück`

export const getQuantityDiscountCartPreviewText = (
  quantityDiscount: QuantityDiscount | undefined,
  cartQty: number,
  addQty: number,
): QuantityDiscountText | undefined => {
  const normalizedQuantityDiscount = normalizeQuantityDiscount(quantityDiscount)
  if (!normalizedQuantityDiscount) return undefined

  const safeCartQty = Math.max(0, Math.floor(cartQty))
  const safeAddQty = Math.max(0, Math.floor(addQty))
  const previewQty = safeCartQty + safeAddQty
  const previewText = getQuantityDiscountText(normalizedQuantityDiscount, previewQty)
  if (!previewText || safeCartQty <= 0) return previewText

  const triggerQuantity = getQuantityDiscountTriggerQuantity(normalizedQuantityDiscount)
  const currentApplications = getApplications(safeCartQty, normalizedQuantityDiscount)
  const previewApplications = getApplications(previewQty, normalizedQuantityDiscount)

  if (previewApplications <= 0) {
    const missingQty = Math.max(0, triggerQuantity - previewQty)
    return {
      ...previewText,
      progress: normalizedQuantityDiscount.rewardType === 'freeItems'
        ? `Du hast ${formatPieceCount(safeCartQty)} im Warenkorb. Noch ${formatPieceCount(missingQty)} dazu bis zur Gratis-Aktion.`
        : `Du hast ${formatPieceCount(safeCartQty)} im Warenkorb. Noch ${formatPieceCount(missingQty)} dazu bis zum Aktionsrabatt.`,
      active: false,
    }
  }

  if (normalizedQuantityDiscount.rewardType !== 'freeItems') {
    return {
      ...previewText,
      progress: currentApplications < previewApplications
        ? `Du hast ${formatPieceCount(safeCartQty)} im Warenkorb. Mit dieser Auswahl wird der Aktionsrabatt aktiv.`
        : `Der Aktionsrabatt ist im Warenkorb aktiv.`,
      active: true,
    }
  }

  const currentFreeQty = getDiscountedRewardQuantity(safeCartQty, normalizedQuantityDiscount)
  const previewFreeQty = getDiscountedRewardQuantity(previewQty, normalizedQuantityDiscount)
  const newFreeQty = Math.max(0, previewFreeQty - currentFreeQty)

  if (currentApplications < previewApplications && newFreeQty > 0) {
    const allAddedItemsAreFree = newFreeQty === safeAddQty

    return {
      ...previewText,
      progress: allAddedItemsAreFree
        ? `Du hast ${formatPieceCount(safeCartQty)} im Warenkorb. ${newFreeQty === 1 ? 'Dieses Stück ist' : `Diese ${formatPieceCount(newFreeQty)} sind`} gratis, wenn du ${newFreeQty === 1 ? 'es' : 'sie'} jetzt hinzufügst.`
        : `Du hast ${formatPieceCount(safeCartQty)} im Warenkorb. Mit dieser Auswahl wird ${formatPieceCount(newFreeQty)} gratis.`,
      active: true,
      actionLabel: allAddedItemsAreFree
        ? newFreeQty === 1 ? 'Gratis-Stück hinzufügen' : 'Gratis-Stücke hinzufügen'
        : undefined,
    }
  }

  return {
    ...previewText,
    progress: `Im Warenkorb ${previewFreeQty === 1 ? 'ist' : 'sind'} ${formatPieceCount(previewFreeQty)} gratis.`,
    active: true,
  }
}
